<template>
  <div class="project-page" ref="mainElement">
    <main v-if="data" class="main">
      <ProjectHeader
        v-if="project"
        :title="project.title ?? ''"
        :image="project.image as DatoAssetDto"
        :image-small="project.smallImage as DatoAssetDto"
        :service-name="project?.service?.title ?? ''"
      />
      <div class="inner">
        <ProjectFacts
          :title="project?.factsTitle ?? undefined"
          :text-fields="project?.textFields"
          :description="project?.factsDescription"
          :facts="project?.projectFacts"
        />
        <component
          v-for="module in project?.contentModules"
          :key="module.id"
          :is="ContentModules[module.__typename]"
          :data="module"
        >
        </component>
        <div class="divider"></div>
        <ServiceContentProjects v-if="project?.service" :service="project.service" />
      </div>
    </main>
    <SiteFooter theme="dark" />
  </div>
</template>

<script setup lang="ts">
import gsap from 'gsap';
import { ContentModules } from '~/components/content-modules/ContentModules';
import ProjectHeader from '~/components/project-modules/ProjectHeader.vue';
import ServiceContentProjects from '~/components/service-modules/ServiceContentProjects.vue';
import type { GetProjectBySlugQuery } from '#gql';
import type { DatoAssetDto } from '~/dato/dato';
import ProjectFacts from '~/components/project-modules/ProjectFacts.vue';
import { type SeoMetaTagType, toHead } from 'vue-datocms';
import type { ServiceFragment } from '#gql';

const props = defineProps<{}>();

const { params } = useRoute();

const { data, error } = await useAsyncGql({
  operation: 'GetProjectBySlug',
  variables: { slug: String(params.projekt_id) }
});

const project = (data.value as GetProjectBySlugQuery).project;

if (!project || error.value) {
  showError({
    statusCode: 404
  });
}

if (project?.service) {
  project.service.featuredProjects = project.service.featuredProjects.filter(
    (sProject) => sProject.id !== project.id
  ); // don't show same project in service featured projects
}

const mainElement = ref<HTMLElement | null>(null);

useHead(toHead(project?.seo as SeoMetaTagType[]));

definePageMeta({
  pageTransition: {
    name: 'project-page',
    mode: 'in-out',
    onLeave: function (el, done) {
      gsap.to(el, {
        opacity: 0,
        onComplete: done
      });
    },
    onEnter: async function (el, done) {
      await useSleep(50);

      const heroAssetElement = el.querySelector('.project-header .dato-asset ');
      const heroImageElement = heroAssetElement?.querySelector('.image ');
      const assetElement = document.querySelector('.-in-transition .asset');

      if (!assetElement || !heroAssetElement) {
        if (el) {
          gsap.fromTo(
            el,
            {
              opacity: 0
            },
            {
              opacity: 1,
              duration: 1.25,
              ease: 'power2.in',
              onComplete: done
            }
          );
        } else {
          return done();
        }
      } else {
        const box = assetElement.getBoundingClientRect();

        gsap.set(el, {
          overflow: 'clip',
          maxHeight: '100svh'
        });

        const onComplete = function () {
          gsap.set(el, {
            overflow: 'unset',
            maxHeight: 'unset'
          });
          tl.kill();
          done();
        };

        const tl = gsap.timeline({
          onComplete
        });

        gsap.set(heroAssetElement, {
          x: box.x,
          y: box.y,
          width: box.width,
          height: box.height,
          '--brightness': 1
          // filter: 'brightness(1)'
        });

        tl.fromTo(
          heroAssetElement,
          {
            x: box.x,
            y: box.y,
            width: box.width,
            height: box.height,
            '--brightness': 1
            // filter: 'brightness(1)'
          },
          {
            x: 0,
            y: 0,
            width: window.innerWidth,
            height: window.innerHeight,
            '--brightness': 0.5,
            // filter: 'brightness(0.5)',
            duration: 0.75,
            delay: 0.75,
            ease: 'power4.out'
            // ease: 'circ.out'
          }
        );
      }

      return done();
    }
  }
});
</script>

<style scoped lang="scss">
.project-page-enxter-active,
.project-page-leave-active {
}

.project-page-leave-to {
}

.project-page {
  > .main {
    max-width: 100%;
    overflow-x: clip;
    margin-left: var(--site-outer-margin);
    margin-right: var(--site-outer-margin);

    > .inner {
      background-color: var(--color-off-white);
      z-index: 10;
      position: relative;

      > .divider {
        background-color: var(--color-off-white);

        @media (--vs) {
          height: 0;
        }
        @media (--vl) {
          height: 6rem;
        }
      }
    }
  }
}
</style>
